import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  DraftOverrideCategory,
  IDraftRecommendation,
  useAcceptContractModelDraftRecommendationMutation,
  useGetContractModelDraftRunQuery,
  useOverrideContractModelDraftRecommendationMutation,
  usePresignContractModelDraftDocumentMutation,
  useRejectContractModelDraftRecommendationMutation,
  useRunContractModelDraftGenerationMutation,
} from "store/queries/contractModelDrafts";

import {
  ALLOWED_UPLOAD_EXTENSIONS,
  confidenceSeverity,
  generateHint,
  groupByComponentType,
  isProcessing,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
} from "./utils";

// Temporary: restrict this page to a single user while it's in early access.
const ALLOWED_EMAIL = "mike.over@clearhealthinc.com";

const OVERRIDE_CATEGORIES: { label: string; value: DraftOverrideCategory }[] = [
  { label: "Agent misread the source", value: "agent_misread_source" },
  { label: "Agent missed a document", value: "agent_missed_document" },
  {
    label: "Correct read, wrong policy mapping",
    value: "wrong_policy_mapping",
  },
  { label: "Contract is ambiguous", value: "contract_ambiguous" },
];

function AttributionBadges({ rec }: { rec: IDraftRecommendation }) {
  if (rec.attributions.length === 0) {
    return <span className="text-500 text-sm">No source attribution</span>;
  }
  return (
    <div className="flex flex-column gap-1">
      {rec.attributions.map((a) => (
        <div key={a.id} className="flex align-items-start gap-2">
          <Tag
            severity={a.verified ? "success" : "danger"}
            value={
              a.verified ? `verified (${a.verificationMethod})` : "unverified"
            }
          />
          <span className="text-sm">
            <em>&ldquo;{a.exactQuote}&rdquo;</em>
            {a.locator ? (
              <span className="text-500"> — {a.locator}</span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function RecommendationCard({
  rec,
  onAccept,
  onReject,
  onOverride,
  busy,
}: {
  rec: IDraftRecommendation;
  onAccept: () => void;
  onReject: () => void;
  onOverride: () => void;
  busy: boolean;
}) {
  return (
    <Card className="mb-3">
      <div className="flex justify-content-between align-items-center mb-2">
        <div className="flex align-items-center gap-2">
          <span className="font-bold">{rec.componentType}</span>
          <Tag
            severity={confidenceSeverity(rec.confidence)}
            value={rec.confidence}
          />
          <Tag value={rec.status} />
        </div>
      </div>
      <pre
        className="surface-100 p-2 border-round text-sm overflow-auto"
        style={{ margin: 0 }}
      >
        {JSON.stringify(rec.proposedModeling, null, 2)}
      </pre>
      {rec.missingInputs ? (
        <div className="mt-2 text-sm">
          <span className="font-semibold">Needs input:</span>{" "}
          {JSON.stringify(rec.missingInputs)}
        </div>
      ) : null}
      <div className="mt-2">
        <AttributionBadges rec={rec} />
      </div>
      {rec.override ? (
        <div className="mt-2 surface-50 p-2 border-round text-sm">
          <span className="font-semibold">
            Override ({rec.override.category}):
          </span>{" "}
          {rec.override.reason}
        </div>
      ) : null}
      <div className="flex gap-2 mt-3">
        <Button
          label="Accept"
          className="p-button-sm"
          onClick={onAccept}
          disabled={busy}
        />
        <Button
          label="Override"
          className="p-button-sm p-button-outlined p-button-warning"
          onClick={onOverride}
          disabled={busy}
        />
        <Button
          label="Reject"
          className="p-button-sm p-button-outlined p-button-danger"
          onClick={onReject}
          disabled={busy}
        />
      </div>
    </Card>
  );
}

export function ContractModelDraftDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useUserWithAccount();
  const isAllowed = (userInfo?.email ?? "").toLowerCase() === ALLOWED_EMAIL;
  const toastRef = useContext(ToastContext);

  const { data: run } = useGetContractModelDraftRunQuery(id, {
    skip: !isAllowed || !id,
    // Poll while the agents-app worker is processing so the UI reflects progress.
    pollingInterval: id ? 4000 : 0,
  });

  const [presignDoc] = usePresignContractModelDraftDocumentMutation();
  const [runGeneration, { isLoading: generating }] =
    useRunContractModelDraftGenerationMutation();
  const [accept, { isLoading: accepting }] =
    useAcceptContractModelDraftRecommendationMutation();
  const [reject, { isLoading: rejecting }] =
    useRejectContractModelDraftRecommendationMutation();
  const [override, { isLoading: overriding }] =
    useOverrideContractModelDraftRecommendationMutation();
  const busy = accepting || rejecting || overriding;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [overrideTarget, setOverrideTarget] =
    useState<IDraftRecommendation | null>(null);
  const [overrideCategory, setOverrideCategory] =
    useState<DraftOverrideCategory>("wrong_policy_mapping");
  const [overrideReason, setOverrideReason] = useState("");

  const grouped = useMemo(
    () => groupByComponentType(run?.recommendations ?? []),
    [run]
  );

  const toast = (
    severity: "success" | "error",
    summary: string,
    detail: string
  ) => toastRef?.current?.show({ severity, summary, detail, life: 5000 });

  const handleUpload = async (file: File) => {
    const parts = file.name.toLowerCase().split(".");
    const ext = parts.length > 1 ? parts[parts.length - 1] : "";
    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
      toast(
        "error",
        "Unsupported file type",
        `.${
          ext || "?"
        } isn't supported. Allowed: ${ALLOWED_UPLOAD_EXTENSIONS.join(", ")}.`
      );
      return;
    }
    try {
      setUploading(true);
      const { uploadUrl } = await presignDoc({
        runId: id,
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
      }).unwrap();
      const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      toast("success", "Document uploaded", file.name);
    } catch (e) {
      toast(
        "error",
        "Upload failed",
        e instanceof Error ? e.message : "Could not upload the file."
      );
    } finally {
      setUploading(false);
    }
  };

  const submitOverride = async () => {
    if (!overrideTarget) return;
    await override({
      recommendationId: overrideTarget.id,
      category: overrideCategory,
      reason: overrideReason,
      agentWasWrong: true,
    }).unwrap();
    setOverrideTarget(null);
    setOverrideReason("");
  };

  if (!isAllowed) return <Navigate to="/" replace />;

  return (
    <div className="p-4">
      <Button
        label="Back to runs"
        icon="pi pi-arrow-left"
        className="p-button-text p-button-sm mb-2 pl-0"
        onClick={() => navigate("/contract-model-drafts")}
      />

      {!run ? (
        <p className="text-500">Loading run…</p>
      ) : (
        <>
          <div className="flex align-items-center gap-2 mb-1">
            <h1 className="m-0">{runDisplayName(run)}</h1>
            <Tag
              value={runStatusLabel(run.status)}
              severity={runStatusSeverity(run.status)}
            />
          </div>
          <p className="text-500 text-sm mt-0 mb-3">
            Upload the raw payer-contract documents, generate a model, then
            review each recommendation.
          </p>

          <div className="surface-card border-1 surface-border border-round p-3 mb-4">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="font-semibold">
                Documents ({run.documents.length})
              </span>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={ALLOWED_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(",")}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                  e.target.value = "";
                }}
              />
              <Button
                label="Upload document"
                icon="pi pi-upload"
                className="p-button-outlined p-button-sm"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              />
            </div>

            {run.documents.length === 0 ? (
              <p className="text-500 text-sm m-0">
                No documents yet — add the contract PDF/Excel to model from.
              </p>
            ) : (
              <ul className="list-none p-0 m-0">
                {run.documents.map((d) => (
                  <li key={d.id} className="text-sm py-1">
                    <i className="pi pi-file mr-2 text-500" />
                    {d.fileName}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex align-items-center gap-2 mt-3">
              <Button
                label={
                  isProcessing(run.status) ? "Generating…" : "Generate model"
                }
                icon="pi pi-bolt"
                disabled={
                  generating ||
                  run.documents.length === 0 ||
                  isProcessing(run.status)
                }
                onClick={() => runGeneration(run.id)}
              />
              {generateHint(run.documents.length, run.status) && (
                <span className="text-500 text-sm">
                  {generateHint(run.documents.length, run.status)}
                </span>
              )}
            </div>
          </div>

          <h3 className="mt-0 mb-2">Recommendations</h3>
          {run.recommendations.length === 0 ? (
            <p className="text-500">
              {isProcessing(run.status)
                ? "Working on it… recommendations will appear here when the model finishes."
                : "None yet. Add documents above and click “Generate model”."}
            </p>
          ) : (
            Object.entries(grouped).map(([type, recs]) => (
              <div key={type} className="mb-4">
                <h4 className="mt-0 mb-2">{type}</h4>
                {recs.map((rec) => (
                  <RecommendationCard
                    key={rec.id}
                    rec={rec}
                    busy={busy}
                    onAccept={() => accept({ recommendationId: rec.id })}
                    onReject={() => reject(rec.id)}
                    onOverride={() => setOverrideTarget(rec)}
                  />
                ))}
              </div>
            ))
          )}
        </>
      )}

      <Dialog
        header="Override recommendation"
        visible={!!overrideTarget}
        style={{ width: "40rem" }}
        onHide={() => setOverrideTarget(null)}
      >
        <div className="flex flex-column gap-3">
          <div>
            <label
              className="block mb-2 font-medium"
              htmlFor="override-category"
            >
              Why is the agent wrong?
            </label>
            <Dropdown
              id="override-category"
              className="w-full"
              options={OVERRIDE_CATEGORIES}
              value={overrideCategory}
              onChange={(e) => setOverrideCategory(e.value)}
            />
          </div>
          <div>
            <label className="block mb-2 font-medium" htmlFor="override-reason">
              Correction / reason
            </label>
            <InputTextarea
              id="override-reason"
              className="w-full"
              rows={4}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
            />
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setOverrideTarget(null)}
            />
            <Button
              label="Save override"
              icon="pi pi-check"
              disabled={busy || !overrideReason.trim()}
              onClick={submitOverride}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
